import styled from "styled-components";
import { useLanguage } from "../../contexts/LanguageContext";

type Props = {
  page: number;
  totalPage: number;
  onSetPage: (page: number) => void;
  onPrevious: () => void;
  onNext: () => void;
};

export const Pagination: React.FC<Props> = ({
  page,
  totalPage,
  onSetPage,
  onPrevious,
  onNext,
}) => {
  const { t } = useLanguage();
  const _renderPageItem = (
    page: number,
    isCurrentPage: boolean,
    isEmpty?: boolean,
    wide?: boolean,
  ) => {
    return (
      <StyledPaginationButton
        key={`pagination-${page}`}
        onClick={() => {
          if (!isCurrentPage && !isEmpty && page > 0) onSetPage(page);
        }}
        $isCurrentPage={isCurrentPage}
        $isEmpty={isEmpty}
        $isWide={wide}
      >
        {!isEmpty ? page : "..."}
      </StyledPaginationButton>
    );
  };

  const _renderMultiplePageItems = () => {
    if (totalPage === 0) {
      return <>{_renderPageItem(0, false, true)}</>;
    }

    if (totalPage < 7) {
      return Array.from({ length: totalPage }, (x, i) => i).map((pg) =>
        _renderPageItem(pg + 1, page === pg + 1),
      );
    }

    if ([totalPage - 2, totalPage - 1, totalPage].includes(page)) {
      return (
        <>
          {_renderPageItem(1, false)}
          {_renderPageItem(0, false, true, true)}
          {_renderPageItem(totalPage - 3, false)}
          {_renderPageItem(totalPage - 2, page === totalPage - 2)}
          {_renderPageItem(totalPage - 1, page === totalPage - 1)}
          {_renderPageItem(totalPage, page === totalPage)}
        </>
      );
    }

    if ([1, 2, 3].includes(page)) {
      return (
        <>
          {_renderPageItem(1, page === 1)}
          {_renderPageItem(2, page === 2)}
          {_renderPageItem(3, page === 3)}
          {_renderPageItem(4, false)}
          {_renderPageItem(0, false, true, true)}
          {_renderPageItem(totalPage, false)}
        </>
      );
    }

    return (
      <>
        {_renderPageItem(1, false)}
        {_renderPageItem(0, false, true)}
        {_renderPageItem(page - 1, false)}
        {_renderPageItem(page, true)}
        {_renderPageItem(page + 1, false)}
        {_renderPageItem(0, false, true)}
        {_renderPageItem(totalPage, false)}
      </>
    );
  };

  return (
    <StyledPaginationContainer>
      <StyledPaginationButton
        onClick={onPrevious}
        $disabled={totalPage === 0 || page === 1}
        $isWide
      >
        {t('common.previous')}
      </StyledPaginationButton>

      <StyledPaginationButtonContainer>
        {_renderMultiplePageItems()}
      </StyledPaginationButtonContainer>

      <StyledPaginationButton
        onClick={onNext}
        $disabled={totalPage === 0 || page === totalPage}
        $isWide
      >
        {t('common.next')}
      </StyledPaginationButton>
    </StyledPaginationContainer>
  );
};

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px 0;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (min-width: 768px) {
    margin: 32px 0;
    gap: 12px;
  }
  
  @media (min-width: 1024px) {
    margin: 40px 0;
    gap: 16px;
  }
`;

const StyledPaginationButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 8px;
  
  @media (min-width: 768px) {
    gap: 6px;
    padding: 6px 12px;
  }
  
  @media (min-width: 1024px) {
    gap: 8px;
    padding: 6px 18px;
  }
`;

const StyledPaginationButton = styled.div<{
  $disabled?: boolean;
  $isCurrentPage?: boolean;
  $isEmpty?: boolean;
  $isWide?: boolean;
}>`
  min-width: ${({ $isWide }) => ($isWide ? "50px" : "28px")};
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 14px;
  font-weight: ${({ $isCurrentPage }) => ($isCurrentPage ? 700 : 500)};
  line-height: 1;
  margin: 0 1px;
  cursor: ${({ $isEmpty, $disabled }) =>
    $isEmpty || $disabled ? "not-allowed" : "pointer"};
  background: ${({ $isCurrentPage }) =>
    $isCurrentPage ? "#18145a" : "transparent"};
  color: ${({ $isCurrentPage, $isEmpty, $disabled }) => {
    if ($isCurrentPage) return "#fff";
    if ($isEmpty) return "#B3B3B3";
    if ($disabled) return "#B3B3B3";
    return "#18145a";
  }};
  border: none;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    min-width: ${({ $isWide }) => ($isWide ? "60px" : "32px")};
    height: 32px;
    font-size: 15px;
    margin: 0 2px;
  }
  
  @media (min-width: 1024px) {
    min-width: ${({ $isWide }) => ($isWide ? "70px" : "36px")};
    height: 36px;
    font-size: 16px;
    margin: 0 2px;
  }
  
  &:hover {
    background: ${({ $isCurrentPage, $isEmpty, $disabled }) =>
    !$isCurrentPage && !$isEmpty && !$disabled ? "#f0f0f0" : undefined};
  }
  
  ${({ $isEmpty }) =>
    $isEmpty &&
    `
      pointer-events: none;
      background: transparent;
      font-weight: 500;
      cursor: default;
    `}
  ${({ $disabled }) =>
    $disabled &&
    `
      pointer-events: none;
      background: transparent;
      color: #B3B3B3;
    `}
`;
